import type { ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { getRoleLabel } from "@/core/guild-events/labels"
import {
  GUILD_EVENTS_SCHEDULE_CONFIG,
  GUILD_EVENT_SCHEDULE_WEEKDAYS,
  WWM_EVENT_ROLE_CATALOG,
  type GuildEventScheduleWeekday,
  type WwmEventRoleKey,
} from "@/core/guild-events/types"

const DEFAULT_RAID_ROLES: WwmEventRoleKey[] = ["tank", "dps", "healer"]
const DAY_PRESETS = [
  { label: "Every day", weekdays: GUILD_EVENT_SCHEDULE_WEEKDAYS },
  {
    label: "Weekdays",
    weekdays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  },
  { label: "Weekends", weekdays: ["saturday", "sunday"] },
] as const satisfies ReadonlyArray<{
  label: string
  weekdays: readonly GuildEventScheduleWeekday[]
}>
export type ScheduleFormState =
  | {
      title: string
      description: string
      eventKind: "raid"
      eventRoles: WwmEventRoleKey[]
      weekdays: GuildEventScheduleWeekday[]
      startTime: string
      signupCloseTime: string
      rsvpEnabled: boolean
    }
  | {
      title: string
      description: string
      eventKind: "attendance"
      eventRoles: []
      weekdays: GuildEventScheduleWeekday[]
      startTime: string
      signupCloseTime: null
      rsvpEnabled: boolean
    }
export const createScheduleFormState = (): ScheduleFormState => ({
  title: "",
  description: "",
  eventKind: "raid",
  eventRoles: DEFAULT_RAID_ROLES,
  weekdays: [],
  startTime: "",
  signupCloseTime: "",
  rsvpEnabled: true,
})
type Field<T> = (
  children: (field: {
    state: { value: T; meta: { errors: unknown[] } }
    handleBlur: () => void
    handleChange: (value: T) => void
  }) => ReactNode,
) => ReactNode
export type ScheduleFormBindings = {
  values: ScheduleFormState
  eventKind: Field<"raid" | "attendance">
  title: Field<string>
  description: Field<string>
  eventRoles: Field<WwmEventRoleKey[]>
  weekdays: Field<GuildEventScheduleWeekday[]>
  startTime: Field<string>
  signupCloseTime: Field<string>
  rsvpEnabled: Field<boolean>
  setEventRoles: (value: WwmEventRoleKey[]) => void
  setSignupCloseTime: (value: string | null) => void
}
type Errors = Partial<
  Record<
    "title" | "weekdays" | "startTime" | "eventRoles" | "signupCloseTime",
    string
  >
>
const firstError = (errors: unknown[]) =>
  typeof errors[0] === "string"
    ? errors[0]
    : errors[0] instanceof Error
      ? errors[0].message
      : null
function FieldError({ error, id }: { error: string | null; id: string }) {
  return error ? (
    <p id={id} className="text-sm text-destructive">
      {error}
    </p>
  ) : null
}

export function ScheduleFormFields({
  bindings,
}: {
  bindings: ScheduleFormBindings
}) {
  return (
    <div className="grid gap-5">
      <section
        aria-labelledby="schedule-template-heading"
        className="grid gap-4 rounded-xl border bg-muted/20 p-4 md:p-5"
      >
        <div className="grid gap-1">
          <h3 id="schedule-template-heading" className="font-semibold">
            Event template
          </h3>
          <p className="text-sm text-muted-foreground">
            Define what members will see each time this schedule generates an
            event.
          </p>
        </div>
        <div className="grid gap-4">
          {bindings.title((field) => {
            const error = firstError(field.state.meta.errors)
            const errorId = "schedule-title-error"
            return (
              <div className="grid gap-2">
                <Label htmlFor="schedule-title">Title</Label>
                <Input
                  id="schedule-title"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                  maxLength={160}
                  required
                />
                <FieldError error={error} id={errorId} />
              </div>
            )
          })}
          {bindings.eventKind((field) => (
            <fieldset className="grid gap-2">
              <legend className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Event Kind
              </legend>
              <RadioGroup
                value={field.state.value}
                onValueChange={(next) => {
                  if (next === "attendance") {
                    field.handleChange("attendance")
                    bindings.setEventRoles([])
                    bindings.setSignupCloseTime(null)
                    return
                  }
                  field.handleChange("raid")
                  bindings.setEventRoles(DEFAULT_RAID_ROLES)
                  bindings.setSignupCloseTime("")
                }}
                className="flex flex-wrap gap-2"
              >
                <Label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm has-data-[checked]:border-primary has-data-[checked]:bg-primary has-data-[checked]:text-primary-foreground">
                  <RadioGroupItem value="raid" />
                  Raid
                </Label>
                <Label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm has-data-[checked]:border-primary has-data-[checked]:bg-primary has-data-[checked]:text-primary-foreground">
                  <RadioGroupItem value="attendance" />
                  Attendance
                </Label>
              </RadioGroup>
            </fieldset>
          ))}
        </div>
        {bindings.description((field) => (
          <div className="grid gap-2">
            <Label htmlFor="schedule-description">Description</Label>
            <Textarea
              id="schedule-description"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(event) => field.handleChange(event.target.value)}
              maxLength={2_000}
              placeholder="Optional details for members"
            />
          </div>
        ))}
        {bindings.values.eventKind === "raid"
          ? bindings.eventRoles((field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = "schedule-event-roles-error"
              return (
                <fieldset
                  className="grid gap-2 border-l-2 border-primary/30 pl-4"
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? errorId : undefined}
                >
                  <legend className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                    Raid roles
                  </legend>
                  <p className="text-xs text-muted-foreground">
                    Available to members signing up for this raid.
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-3">
                    {(
                      Object.keys(WWM_EVENT_ROLE_CATALOG) as WwmEventRoleKey[]
                    ).map((roleKey) => (
                      <Label
                        key={roleKey}
                        className="flex cursor-pointer items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={field.state.value.includes(roleKey)}
                          onBlur={field.handleBlur}
                          onCheckedChange={(checked) =>
                            field.handleChange(
                              checked
                                ? [...field.state.value, roleKey]
                                : field.state.value.filter(
                                    (role) => role !== roleKey,
                                  ),
                            )
                          }
                        />
                        {getRoleLabel(roleKey)}
                      </Label>
                    ))}
                  </div>
                  <FieldError error={error} id={errorId} />
                </fieldset>
              )
            })
          : null}
        {bindings.rsvpEnabled((field) => (
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={field.state.value}
              onBlur={field.handleBlur}
              onCheckedChange={(checked) => field.handleChange(checked)}
            />
            <span className="grid gap-1">
              <span className="font-medium">Enable RSVPs</span>
              <span className="text-muted-foreground">
                Members can RSVP and view the roster.
              </span>
            </span>
          </label>
        ))}
      </section>
      <section
        aria-labelledby="schedule-recurrence-heading"
        className="grid gap-4 rounded-xl border bg-muted/20 p-4 md:p-5"
      >
        <div className="grid gap-1">
          <h3 id="schedule-recurrence-heading" className="font-semibold">
            Recurrence
          </h3>
          <p className="text-sm text-muted-foreground">
            Choose when this template repeats.
          </p>
        </div>
        {bindings.weekdays((field) => {
          const error = firstError(field.state.meta.errors)
          const errorId = "schedule-weekdays-error"
          return (
            <fieldset
              className="grid gap-4"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
            >
              <legend className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Quick selection
              </legend>
              <div className="grid gap-2">
                <div className="flex flex-wrap gap-2">
                  {DAY_PRESETS.map((preset) => {
                    const isActive =
                      preset.weekdays.length === field.state.value.length &&
                      preset.weekdays.every((weekday) =>
                        field.state.value.includes(weekday),
                      )
                    return (
                      <Button
                        key={preset.label}
                        type="button"
                        size="sm"
                        variant={isActive ? "secondary" : "outline"}
                        className={
                          isActive
                            ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                            : undefined
                        }
                        aria-pressed={isActive}
                        onClick={() => field.handleChange([...preset.weekdays])}
                      >
                        {preset.label}
                      </Button>
                    )
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Apply a common pattern, then fine-tune individual days below.
                </p>
              </div>
              <div className="grid gap-2">
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  Individual days
                </p>
                <div className="flex flex-wrap gap-x-5 gap-y-3">
                  {GUILD_EVENT_SCHEDULE_WEEKDAYS.map((weekday) => (
                    <Label
                      key={weekday}
                      className="flex cursor-pointer items-center gap-2 text-sm capitalize"
                    >
                      <Checkbox
                        checked={field.state.value.includes(weekday)}
                        onBlur={field.handleBlur}
                        onCheckedChange={(checked) =>
                          field.handleChange(
                            checked === true
                              ? [...field.state.value, weekday]
                              : field.state.value.filter(
                                  (item) => item !== weekday,
                                ),
                          )
                        }
                      />
                      {weekday}
                    </Label>
                  ))}
                </div>
              </div>
              <FieldError error={error} id={errorId} />
            </fieldset>
          )
        })}
        <fieldset className="grid gap-4">
          <legend className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Timing
          </legend>
          <div className="grid gap-1">
            <p
              id="schedule-timezone-hint"
              className="text-xs text-muted-foreground"
            >
              Use 24-hour Central European time (CEST/CET).
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {bindings.startTime((field) => {
              const error = firstError(field.state.meta.errors)
              const errorId = "schedule-start-time-error"
              const describedBy = [
                "schedule-timezone-hint",
                "schedule-start-time-hint",
                error ? errorId : null,
              ]
                .filter(Boolean)
                .join(" ")
              return (
                <div className="grid gap-2">
                  <Label htmlFor="schedule-start-time">Event start</Label>
                  <Input
                    id="schedule-start-time"
                    type="time"
                    step={60}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    required
                  />
                  <p
                    id="schedule-start-time-hint"
                    className="text-xs text-muted-foreground"
                  >
                    After daily creation at{" "}
                    {GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime}.
                  </p>
                  <FieldError error={error} id={errorId} />
                </div>
              )
            })}
            {bindings.values.eventKind === "raid"
              ? bindings.signupCloseTime((field) => {
                  const error = firstError(field.state.meta.errors)
                  const errorId = "schedule-close-time-error"
                  const describedBy = [
                    "schedule-timezone-hint",
                    "schedule-close-time-hint",
                    error ? errorId : null,
                  ]
                    .filter(Boolean)
                    .join(" ")
                  return (
                    <div className="grid gap-2">
                      <Label htmlFor="schedule-close-time">Signup close</Label>
                      <Input
                        id="schedule-close-time"
                        type="time"
                        step={60}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={describedBy}
                        value={field.state.value ?? ""}
                        onBlur={field.handleBlur}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        required
                      />
                      <p
                        id="schedule-close-time-hint"
                        className="text-xs text-muted-foreground"
                      >
                        After daily creation at{" "}
                        {GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime} and
                        before the event starts.
                      </p>
                      <FieldError error={error} id={errorId} />
                    </div>
                  )
                })
              : null}
          </div>
        </fieldset>
      </section>
    </div>
  )
}

export function validateScheduleInput(value: ScheduleFormState) {
  const fields: Errors = {}
  if (!value.title.trim()) fields.title = "Title is required."
  if (value.weekdays.length === 0)
    fields.weekdays = "Select at least one weekday."
  if (!isValidTime(value.startTime))
    fields.startTime = "A valid start time is required."
  else if (value.startTime <= GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime)
    fields.startTime = `Start time must be after ${GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime}.`
  if (value.eventKind === "raid") {
    if (value.eventRoles.length === 0)
      fields.eventRoles = "Select at least one raid role."
    if (!isValidTime(value.signupCloseTime))
      fields.signupCloseTime = "A valid signup close time is required."
    else if (
      value.signupCloseTime <= GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime
    )
      fields.signupCloseTime = `Signup close time must be after ${GUILD_EVENTS_SCHEDULE_CONFIG.eventCreationJobTime}.`
    else if (value.signupCloseTime >= value.startTime)
      fields.signupCloseTime =
        "Signup close time must be before the event start time."
  }
  return Object.keys(fields).length > 0 ? { fields } : undefined
}
function isValidTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
}
